import { Song } from "../components/view/spotipitan/types";
import { auth } from "../firebase";
import { getSongFromDB, getSongPosterFromDB } from "./dbUtils";
import { AlbumDTO, AlbumMotionArtworkResponse, CanvasVideoUrlResponse, CreateAlbumPayload, DeleteSongResponse, FunctionName, PaginatedResponse, ResolveTrackQuery, UnifiedTrack, UpdateAlbumPayload, UpdateSongPayload } from "./apiTypes";

const isProduction = typeof window !== 'undefined' && (
    window.location.hostname === 'spotip17an.es' ||
    window.location.hostname === 'www.spotip17an.es' ||
    window.location.hostname === 'spotip17an.web.app' ||
    window.location.hostname === 'spotip17an.firebaseapp.com'
);

export const FUNCTIONS_BACKEND = process.env.REACT_APP_FUNCTIONS_BACKEND || (
    isProduction
        ? 'https://torresowo.myftp.org/'
        : 'https://torresowo.myftp.org:7777/spotip17an-test/'
);


export const BACKEND_URL = 'https://torresowo.myftp.org:8082/';
export const API_POST_FILE = 'uploadFile';
export const API_POST_CREATE_OR_GET_FOLDER = "cap17drive/v2/get-or-create-folder/:folderName";
export const API_POST_SONG = "spotip17an/v2/post-song";

export class CustomApi {

    static readonly post = async (url: string, params?: Record<string, string>, query?: Record<string, string>, body?: Record<string, any>) => {
        let parsedUrl = this.getParsedUrl(url, params, query);
        const response = await fetch(`${BACKEND_URL}${parsedUrl}`, {
            body: JSON.stringify(body),
            method: "POST",
            headers: {
                'Content-Type': 'application/json'
            },
        })
        if (response.ok) {
            return response.json();
        } else {
            throw new Error(response.statusText);
        }
    }

    static uploadFile(file: File, fileName: string, userToken: string, folderId: number,
        onFinish: (this: XMLHttpRequest, ev: ProgressEvent<XMLHttpRequestEventTarget>) => any,
        onError: (this: XMLHttpRequest, ev: ProgressEvent<XMLHttpRequestEventTarget>) => any) {
        const data = new FormData();
        data.append('title', file.name);
        data.append('file', file);
        data.append('token', userToken);
        data.append('name', fileName);
        data.append('folder', folderId + "");
        if (file && fileName) {
            const ajax = new XMLHttpRequest();
            ajax.addEventListener("load", onFinish, false);
            ajax.addEventListener("error", onError, false);

            ajax.open("POST", BACKEND_URL + API_POST_FILE);
            ajax.send(data);
        }
    }

    private static getParsedUrl = (url: string, params?: Record<string, string>, query?: Record<string, string>) => {
        let parsedUrl = url;
        if (params !== undefined) {
            for (const key in params) {
                parsedUrl = parsedUrl.replace(`:${key}`, params[key]);
            }
        }
        if (query !== undefined) {
            let queryStr = '?';
            for (const key in query) {
                queryStr += `${key}=${query[key]}&`;
            }
            if (queryStr.endsWith('&')) {
                queryStr = queryStr.slice(0, -1);
                parsedUrl += queryStr;
            }
        }

        return parsedUrl;
    }


}

export const getYoutubeVideoUrl = (id: string) => {
    return Spotip17anFunctions.use('fetchVideoMp4').getUrl({ videoId: id });
}

export interface ApiErrorPayload {
    error?: boolean;
    code?: string;
    message?: string;
    status?: number;
}

// create custom Error
export class ApiCallError extends Error {
    public details?: ApiErrorPayload;
    constructor(message: string, public code: number) {
        super(message);
        try {
            this.details = JSON.parse(message);
        } catch { }
    }
}

class Spotip17anFunction {

    private functionName?: FunctionName;
    private timeout?: number;

    constructor(functionName: FunctionName, timeout?: number) {
        this.functionName = functionName;
        this.timeout = timeout;
    }

    getUrl(params?: Record<string, string>) {
        return `${FUNCTIONS_BACKEND}spotip17an/v3/call-function/${this.functionName}?${new URLSearchParams(params).toString()}`;
    }

    async post(params?: Record<string, any>) {
        return this.call(params, 'POST');
    }

    async get(params?: Record<string, string>) {
        return this.call(params, 'GET');
    }

    async call(params?: Record<string, any>, method: 'POST' | 'GET' = 'POST'): Promise<any | Response | PaginatedResponse<any>> {
        if (!this.functionName) {
            throw new Error("Function name not set");
        }

        const bearerToken = auth.currentUser ? await auth.currentUser.getIdToken() : "";

        const options: RequestInit = {
            method: method,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${bearerToken}`,
            },
        }

        if (method === 'POST') {
            options.body = JSON.stringify(params);
        }

        let queryParams = "";
        if (method === 'GET') {
            queryParams = `?${new URLSearchParams(params).toString()}`;
        }

        let responsePromise = fetch(`${FUNCTIONS_BACKEND}spotip17an/v3/call-function/${this.functionName}${queryParams}`, options)

        if (this.timeout) {
            const timeoutPromise: Promise<Response> = new Promise((resolve) => {
                setTimeout(() => {
                    resolve({
                        ok: false,
                        status: 408,
                        statusText: "Timeout",
                        json: () => Promise.resolve({
                            error: "Timeout",
                        }),
                        text: () => Promise.resolve("Timeout"),
                    } as Response);
                }, this.timeout);
            });
            responsePromise = Promise.race([responsePromise, timeoutPromise]);
        }

        const response = await responsePromise;
        if (response.ok) {
            if (response.headers.get('content-type')?.includes('application/json')) {
                return response.json();
            }
            return response;
        } else {
            throw new ApiCallError(await response.text(), response.status);
        }

    }

    // Add this method to support FormData uploads
    callWithFormData(formData: FormData) {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('POST', this.getUrl());

            // Add authentication token if needed
            const tokenPromise = auth.currentUser ? auth.currentUser.getIdToken() : new Promise((resolve) => resolve(""));

            tokenPromise.then(token => {

                if (token) {
                    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
                }

                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        resolve(JSON.parse(xhr.responseText));
                    } else {
                        reject({
                            error: `HTTP Error: ${xhr.statusText}`,
                            status: xhr.status
                        });
                    }
                };

                xhr.onerror = () => {
                    reject({
                        error: 'Network Error',
                        status: xhr.status
                    });
                };

                xhr.send(formData);
            });
        });
    }
}

export class Spotip17anFunctions {
    static readonly use = (functionName: FunctionName, timeout?: number) => {
        return new Spotip17anFunction(functionName, timeout);
    }
}

const blobsCache: Record<string, string> = {};

const canvasCache = new Map<string, CanvasVideoUrlResponse>();
const resolvedTrackCache = new Map<string, UnifiedTrack>();

export const clearSongBlobsCache = (songId: string | number) => {
    const sId = String(songId);
    delete blobsCache[sId + "_audio"];
    delete blobsCache[sId + "_poster"];
    delete blobsCache[sId + "_big"];
    delete blobsCache[sId + "_small"];
    canvasCache.delete(sId);
    resolvedTrackCache.delete(`song_${sId}`);
};

export const getCachedResolvedTrack = (song?: Song | null): UnifiedTrack | null => {
    if (!song?.id) return null;
    return resolvedTrackCache.get(`song_${song.id}`) || null;
};

export const getSongAudioUrlSync = (song?: Song | null): string => {
    if (!song) return '';
    const cachedBlob = blobsCache[song.id.toString() + "_audio"];
    if (cachedBlob) {
        return cachedBlob;
    }
    if (song.song_url) {
        return normalizeFileUrl(song.song_url);
    }
    if (song.file_url) {
        return normalizeFileUrl(song.file_url);
    }
    if (song.id) {
        return `${FUNCTIONS_BACKEND.replace(/\/$/, '')}/spotip17an/v3/call-function/fetchYoutubeSong?id=${song.id}`;
    }
    return '';
};

export const getSongArtworkUrlSync = (song?: Song | null): string => {
    if (!song) return 'https://spotip17an.es/logo512.png';
    const cachedResolved = getCachedResolvedTrack(song);
    const resolvedCover = cachedResolved?.coverUrl || cachedResolved?.albumCoverUrl;
    if (resolvedCover && !resolvedCover.startsWith('blob:') && !resolvedCover.startsWith('data:')) {
        return normalizeFileUrl(resolvedCover);
    }
    if (song.poster_url && !song.poster_url.startsWith('blob:') && !song.poster_url.startsWith('data:')) {
        return normalizeFileUrl(song.poster_url);
    }
    const rawId = String(song.id ?? '').trim();
    if (rawId.length === 11 && isNaN(Number(rawId))) {
        return `https://img.youtube.com/vi/${rawId}/hqdefault.jpg`;
    }
    if (rawId && Number(rawId) > 0) {
        return `${FUNCTIONS_BACKEND.replace(/\/$/, '')}/spotip17an/v3/call-function/getSongPoster?songId=${rawId}&big=true`;
    }
    return 'https://spotip17an.es/logo512.png';
};

export const normalizeFileUrl = (url?: string | null): string => {
    if (!url) return '';
    if (url.startsWith('blob:') || url.startsWith('data:')) {
        return url;
    }
    if (url.startsWith('/files/')) {
        return `https://torresowo.myftp.org/cap17drive/v2${url}`;
    }
    if (url.startsWith('files/')) {
        return `https://torresowo.myftp.org/cap17drive/v2/${url}`;
    }
    if (url.startsWith('/fetchFile?key=')) {
        return `${FUNCTIONS_BACKEND}spotip17an/v3/call-function${url}`;
    }
    if (url.startsWith('/spotip17an/')) {
        const base = FUNCTIONS_BACKEND.endsWith('/') ? FUNCTIONS_BACKEND.slice(0, -1) : FUNCTIONS_BACKEND;
        return `${base}${url}`;
    }
    if (url.startsWith('spotip17an/')) {
        const base = FUNCTIONS_BACKEND.endsWith('/') ? FUNCTIONS_BACKEND.slice(0, -1) : FUNCTIONS_BACKEND;
        return `${base}/${url}`;
    }
    if (!url.startsWith('http')) {
        return 'https://' + url;
    }
    return url;
};

export const getPlaylistPosterUrl = async (playlist: { id: string | number, poster_url?: string }): Promise<string> => {
    if (blobsCache[playlist.id.toString() + "_poster"]) {
        return blobsCache[playlist.id.toString() + "_poster"];
    }

    const poster = await getPlaylistPoster(playlist, true);
    if (poster && poster.size > 0) {
        const blobUrl = URL.createObjectURL(poster);
        blobsCache[playlist.id.toString() + "_poster"] = blobUrl;
        return blobUrl;
    }

    if (playlist.poster_url) {
        return normalizeFileUrl(playlist.poster_url);
    } else {
        return '';
    }
}

export const getSongImageUrl = async (song: { id?: string | number, poster_url?: string }, big: boolean = false): Promise<string> => {
    const songId = song.id !== undefined && song.id !== null ? String(song.id) : "";
    const cacheKey = (songId ? `${songId}_${big ? "big" : "small"}` : song.poster_url) || "";
    if (cacheKey && blobsCache[cacheKey]) {
        return blobsCache[cacheKey];
    }

    // 1. Try IndexedDB cache first (offline downloaded poster)
    if (songId) {
        try {
            const dbSongPoster = await getSongPosterFromDB(songId);
            if (dbSongPoster && dbSongPoster.size > 0) {
                const blobUrl = URL.createObjectURL(dbSongPoster);
                blobsCache[cacheKey] = blobUrl;
                return blobUrl;
            }
        } catch { }
    }

    // 2. If poster_url is directly available, use it (normalized)
    if (song.poster_url) {
        let finalUrl = song.poster_url;
        if (big && finalUrl.includes("?type")) {
            finalUrl = finalUrl.split("?type")[0];
        }
        return normalizeFileUrl(finalUrl);
    }

    // 3. Fallback: try fetching poster from backend if we only have songId
    if (songId && !songId.startsWith("dz-")) {
        const backendPoster = await getSongImage({ id: songId }, big, false);
        if (backendPoster && backendPoster.size > 0) {
            const blobUrl = URL.createObjectURL(backendPoster);
            blobsCache[cacheKey] = blobUrl;
            return blobUrl;
        }
    }

    return "";
}

export const getSongAudioUrl = async (song: Song): Promise<string> => {

    if (blobsCache[song.id.toString() + "_audio"]) {
        return blobsCache[song.id.toString() + "_audio"];
    }

    const dbSongAudio = await getSongAudio(song, true);

    if (dbSongAudio && dbSongAudio.size > 0) {
        if (blobsCache[song.id.toString() + "_audio"]) {
            return blobsCache[song.id.toString() + "_audio"];
        }
        const blobUrl = URL.createObjectURL(dbSongAudio);
        blobsCache[song.id.toString() + "_audio"] = blobUrl;
        return blobUrl;
    }
    return normalizeFileUrl(song.song_url);
}


export const getPlaylistPoster = async (playlist: { id: string | number }, onlyDB = false): Promise<Blob | undefined> => {
    try {
        const dbSongPoster = await getSongPosterFromDB("playlist_" + playlist.id.toString());
        if (dbSongPoster && dbSongPoster.size > 0) {
            return dbSongPoster;
        }
    } catch (e) {
    }

    if (onlyDB) {
        return undefined;
    }

    return Spotip17anFunctions.use('getPlaylistPoster').get({ playlistId: playlist.id.toString() }).then(res => {
        return res.blob();
    });
}

export const getSongImage = async (song: { id: string | number }, big: boolean = false, onlyDB = false): Promise<Blob | undefined> => {
    const songId = String(song.id);
    try {
        const dbSongPoster = await getSongPosterFromDB(songId);
        if (dbSongPoster && dbSongPoster.size > 0) {
            return dbSongPoster;
        }
    } catch (e) {
    }

    if (onlyDB) {
        return undefined;
    }

    return Spotip17anFunctions.use('getSongPoster').get({ songId, big: big ? "true" : "false" }).then(res => {
        return res.blob();
    }).catch(e => {
        console.error(e);
        return undefined;
    });
}
export const getSongAudio = async (song: Song, onlyDB = false): Promise<Blob | undefined> => {
    try {
        const songDB = await getSongFromDB(song.id);
        if (songDB && songDB.blob && songDB.blob.size > 0) {
            return songDB.blob;
        }
    } catch { }

    if (onlyDB) {
        return undefined;
    }

    return fetch(song.song_url).then(res => {
        if (res.ok) {
            return res.blob();
        } else {
            return undefined;
        }
    }).catch(e => {
        return undefined;
    });


}

export interface SongAudioErrorDetails {
    error: boolean;
    code: string;
    message: string;
    status: number;
}

export const fetchSongAudioError = async (song: Song): Promise<SongAudioErrorDetails | null> => {
    try {
        const audioUrl = getSongAudioUrlSync(song);
        if (!audioUrl) return null;
        const res = await fetch(audioUrl);
        if (!res.ok) {
            const data = await res.json().catch(() => null);
            if (data && data.code) {
                return data;
            }
            return {
                error: true,
                code: res.status === 404 ? 'VIDEO_NOT_FOUND' : res.status === 403 ? 'VIDEO_PRIVATE' : 'DOWNLOAD_ERROR',
                message: res.status === 404 ? 'El video no está disponible o no existe.' : 'Error al procesar el audio.',
                status: res.status
            };
        }
    } catch (e) {
        // Do not falsely report a server failure on background network timeout or fetch interruption
        return null;
    }
    return null;
};

export const saveProfilePhoto = async (formData: FormData): Promise<{ url: string }> => {
    const saveProfilePhoto = Spotip17anFunctions.use('saveProfilePhoto');

    return (await saveProfilePhoto.callWithFormData(formData)) as { url: string };

};

export const getCanvasVideoUrl = async (song: Song): Promise<CanvasVideoUrlResponse> => {
    if (!song?.id) {
        return { error: true, canvasUrl: "" } as CanvasVideoUrlResponse;
    }

    const cacheKey = String(song.id);
    if (canvasCache.has(cacheKey)) {
        return canvasCache.get(cacheKey)!;
    }

    try {
        const query = new URLSearchParams();
        if (song.name) query.set("title", song.name);
        const artist = song.user?.username || (song as any).author_name;
        if (artist) query.set("artist", artist);

        const qStr = query.toString() ? `?${query.toString()}` : "";
        const endpoint = `${FUNCTIONS_BACKEND}spotip17an/v3/call-function/canvas/${encodeURIComponent(String(song.id))}${qStr}`;
        const res = await fetch(endpoint);

        if (!res.ok) {
            const emptyResult = { error: true, canvasUrl: "" } as CanvasVideoUrlResponse;
            canvasCache.set(cacheKey, emptyResult);
            return emptyResult;
        }

        const data: CanvasVideoUrlResponse = await res.json();
        if (data.canvasUrl) {
            data.canvasUrl = normalizeFileUrl(data.canvasUrl);
        }
        canvasCache.set(cacheKey, data);
        return data;
    } catch (e) {
        const emptyResult = { error: true, canvasUrl: "" } as CanvasVideoUrlResponse;
        return emptyResult;
    }
};

const motionArtworkCache = new Map<string, AlbumMotionArtworkResponse>();

export const getAlbumMotionArtwork = async (params: {
    albumId?: string | number;
    albumName?: string;
    artistName?: string;
    upc?: string;
    storefront?: string;
}): Promise<AlbumMotionArtworkResponse> => {
    const cacheKey = params.albumId
        ? String(params.albumId)
        : `${params.albumName || ''}_${params.artistName || ''}_${params.upc || ''}_${params.storefront || 'us'}`;

    if (motionArtworkCache.has(cacheKey)) {
        return motionArtworkCache.get(cacheKey)!;
    }

    try {
        const query = new URLSearchParams();
        if (params.albumName) query.set("albumName", params.albumName);
        if (params.artistName) query.set("artistName", params.artistName);
        if (params.upc) query.set("upc", params.upc);
        if (params.storefront) query.set("storefront", params.storefront);

        const idSegment = params.albumId ? encodeURIComponent(String(params.albumId)) : "auto";
        const qStr = query.toString() ? `?${query.toString()}` : "";
        const endpoint = `${FUNCTIONS_BACKEND}spotip17an/v3/call-function/motion-artwork/${idSegment}${qStr}`;
        const res = await fetch(endpoint);

        if (!res.ok) {
            const emptyResult = { error: true, hasMotionArtwork: false } as AlbumMotionArtworkResponse;
            motionArtworkCache.set(cacheKey, emptyResult);
            return emptyResult;
        }

        const data: AlbumMotionArtworkResponse = await res.json();
        motionArtworkCache.set(cacheKey, data);
        return data;
    } catch {
        const emptyResult = { error: true, hasMotionArtwork: false } as AlbumMotionArtworkResponse;
        return emptyResult;
    }
};

const inFlightResolvePromises = new Map<string, Promise<UnifiedTrack | null>>();

export const isSpotip17anNativeSong = (song?: Song | null): boolean => {
    if (!song) return false;
    if (song.user?.id < 999) return true;
    if (song.file_url) return true;
    if (song.song_url && (song.song_url.includes('cap17drive') || song.song_url.startsWith('files/'))) return true;
    const rawId = String(song.id ?? '').trim();
    if (!isNaN(Number(rawId)) && rawId.length < 4 && Number(rawId) > 0) return true;
    return false;
};

export const resolveSongTrack = async (song?: Song | null): Promise<UnifiedTrack | null> => {
    if (!song?.id) return null;
    const cacheKey = `song_${song.id}`;
    if (resolvedTrackCache.has(cacheKey)) {
        return resolvedTrackCache.get(cacheKey)!;
    }

    // Preserve Spotip17an native artist identity: Never resolve native songs with external services
    if (isSpotip17anNativeSong(song)) {
        const nativeTrack: UnifiedTrack = {
            title: song.name,
            artist: song.author_name || song.user?.username || "",
            artists: song.user ? [{
                id: song.user.id,
                name: song.author_name || song.user.username
            }] : [],
            coverUrl: song.poster_url,
            albumCoverUrl: song.poster_url,
            duration: song.duration,
            resolvedAt: Date.now()
        };
        resolvedTrackCache.set(cacheKey, nativeTrack);
        return nativeTrack;
    }

    if (inFlightResolvePromises.has(cacheKey)) {
        return inFlightResolvePromises.get(cacheKey)!;
    }

    const promise = (async () => {
        try {
            const query: ResolveTrackQuery = {};
            if (typeof song.id === 'string' && song.id.length === 11) {
                query.youtubeVideoId = song.id;
            }
            query.title = song.name;
            query.artist = song.user?.username;

            const res = await Spotip17anFunctions.use('resolveTrack').post(query);
            if (res && !res.error && res.title) {
                resolvedTrackCache.set(cacheKey, res as UnifiedTrack);
                return res as UnifiedTrack;
            }
            return null;
        } catch {
            return null;
        } finally {
            inFlightResolvePromises.delete(cacheKey);
        }
    })();

    inFlightResolvePromises.set(cacheKey, promise);
    return promise;
};

export const createAlbum = async (payload: CreateAlbumPayload): Promise<AlbumDTO> => {
    return Spotip17anFunctions.use('createAlbum').post(payload);
};

export const updateAlbum = async (payload: UpdateAlbumPayload): Promise<AlbumDTO> => {
    return Spotip17anFunctions.use('updateAlbum').post(payload);
};

export const deleteAlbum = async (albumId: string): Promise<{ error: boolean; message: string; albumId?: string }> => {
    return Spotip17anFunctions.use('deleteAlbum').post({ albumId });
};

export const addSongsToAlbum = async (albumId: string, songIds: string[]): Promise<AlbumDTO> => {
    return Spotip17anFunctions.use('addSongsToAlbum').post({ albumId, songIds });
};

export const removeSongFromAlbum = async (albumId: string, songId: string): Promise<AlbumDTO> => {
    return Spotip17anFunctions.use('removeSongFromAlbum').post({ albumId, songId });
};

export const reorderAlbumSongs = async (albumId: string, songIds: string[]): Promise<AlbumDTO> => {
    return Spotip17anFunctions.use('reorderAlbumSongs').post({ albumId, songIds });
};

export const getArtistAlbums = async (artistId: number | string): Promise<AlbumDTO[]> => {
    return Spotip17anFunctions.use('getArtistAlbums').post({ artistId });
};

export const uploadAlbumMotionCover = async (albumId: string, file: File): Promise<{ error: boolean; message: string; motionCoverUrl?: string }> => {
    const formData = new FormData();
    formData.append('albumId', albumId);
    formData.append('file', file);
    return Spotip17anFunctions.use('uploadAlbumMotionCover').callWithFormData(formData) as any;
};

export const updateSong = async (payload: UpdateSongPayload): Promise<Song> => {
    return Spotip17anFunctions.use('updateSong').call(payload);
};

export const deleteSong = async (songId: string | number): Promise<DeleteSongResponse> => {
    return Spotip17anFunctions.use('deleteSong').call({ songId: String(songId) });
};

export const uploadSongCanvas = async (songId: string | number, file: File): Promise<{ error: boolean; message: string; canvasUrl?: string }> => {
    const formData = new FormData();
    formData.append('songId', String(songId));
    formData.append('file', file);
    const res: any = await Spotip17anFunctions.use('uploadSongCanvas').callWithFormData(formData);
    if (res?.canvasUrl) {
        res.canvasUrl = normalizeFileUrl(res.canvasUrl);
    }
    return res;
};